import { Check } from 'lucide-react'
import { JOB_TYPE_LABELS } from '@lib/enums/job-type.enum'
import { PROPOSAL_PLAN_LABELS } from '@lib/enums/proposal-plan.enum'
import { TREE_WORK_CHOICE_PHRASES } from '@lib/enums/tree-work-choice.enum'
import type { ProposalSelectionDto } from '@lib/dto/proposal-selection.dto'
import { formatDateTime } from '@lib/helpers/format'
import {
  formatMoneyAmount,
  formatMoneyWhole,
  parseMoney,
} from '@lib/helpers/money'

/** One line per thing they left out, named the way they saw it. */
function declinedLines(selection: ProposalSelectionDto): string[] {
  const lines: string[] = []
  if (selection.excludedSites.length > 0) {
    lines.push(`Sites: ${selection.excludedSites.join(', ')}`)
  }
  if (selection.excludedProblems.length > 0) {
    lines.push(`Problems: ${selection.excludedProblems.join(', ')}`)
  }
  if (selection.excludedJobTypes.length > 0) {
    lines.push(
      `Work: ${selection.excludedJobTypes
        .map((type) => JOB_TYPE_LABELS[type])
        .join(', ')}`,
    )
  }
  if (selection.excludedSpecies.length > 0) {
    lines.push(`Species: ${selection.excludedSpecies.join(', ')}`)
  }
  if (selection.excludedTrees.length > 0) {
    lines.push(
      `Trees: ${selection.excludedTrees
        .map((tree) => `#${tree.label.replace(/^#/, '')} (${tree.site})`)
        .join(', ')}`,
    )
  }
  if (selection.treeChoices.length > 0) {
    lines.push(
      `Trees: ${selection.treeChoices
        .map(
          (choice) =>
            `#${choice.label.replace(/^#/, '')} (${choice.site}) — ${TREE_WORK_CHOICE_PHRASES[choice.choice]}`,
        )
        .join(', ')}`,
    )
  }
  if (selection.excludedAreaNames.length > 0) {
    lines.push(
      `Areas by name: ${selection.excludedAreaNames
        .map((area) => `${area.name} (${area.site})`)
        .join(', ')}`,
    )
  }
  if (selection.excludedAreas.length > 0) {
    const sites = [...new Set(selection.excludedAreas.map((area) => area.site))]
    const count = selection.excludedAreas.length
    lines.push(`Areas: ${String(count)} on ${sites.join(', ')}`)
  }
  if (selection.excludedIrrigation) {
    lines.push('Irrigation: all of it')
  }
  if (selection.excludedIrrigationKinds.length > 0) {
    lines.push(
      `Kinds of repair: ${selection.excludedIrrigationKinds.join(', ')}`,
    )
  }
  if (selection.excludedZones.length > 0) {
    lines.push(
      `Zones: ${selection.excludedZones
        .map((zone) => `${zone.zone} (${zone.site})`)
        .join(', ')}`,
    )
  }
  if (selection.excludedRepairs.length > 0) {
    // Repairs are chosen by key, not by a name a person would read.
    const sites = [
      ...new Set(selection.excludedRepairs.map((repair) => repair.site)),
    ]
    const count = selection.excludedRepairs.length
    lines.push(
      `Irrigation repairs: ${String(count)} on ${sites.join(', ')}`,
    )
  }
  return lines
}

/**
 * The plan they started from, and the budget it was fitted to.
 *
 * Null when they accepted before plans existed, or adjusted from nothing in
 * particular, in which case the exclusions below are the whole story.
 */
function planLine(selection: ProposalSelectionDto): string | null {
  if (selection.plan === null) return null
  const name = PROPOSAL_PLAN_LABELS[selection.plan]
  const budgetCents = parseMoney(selection.budgetAmount)
  if (budgetCents === null) return `Plan: ${name}`
  // Whole dollars: a board budgets "$9,000", and ".00" reads as false precision.
  return `Plan: ${name}, fitted to a ${formatMoneyWhole(budgetCents)} budget`
}

/**
 * What the client accepted, in their own terms.
 *
 * The answer they gave, not a summary of the proposal: the figure is the one
 * the API computed when they pressed accept, and what they left out is listed
 * because it is the part somebody has to act on — either to quote it again
 * later, or to know not to turn up expecting to do it.
 */
export function ProposalSelectionSummary({
  selection,
}: {
  selection: ProposalSelectionDto
}) {
  const declined = declinedLines(selection)
  const plan = planLine(selection)
  /*
   * A two-year plan leaves the routine trees out on purpose: they are next
   * year's work, agreed in principle, and somebody should quote them again
   * when that budget opens. "Left out" would read as a no.
   */
  const deferredLabel =
    selection.plan === 'PHASED' ? 'Planned for next year' : 'Left out'

  return (
    <div className="space-y-1.5 rounded-md border border-success bg-success-subtle px-3 py-2.5">
      <p className="flex flex-wrap items-center gap-x-2 text-sm font-medium">
        <Check className="h-3.5 w-3.5" />
        Accepted by the client
        <span className="tabular-nums">
          {selection.acceptedAmount === null
            ? ''
            : `— ${formatMoneyAmount(selection.acceptedAmount)}`}
        </span>
        <span className="text-xs font-normal text-muted-text">
          {formatDateTime(selection.acceptedAt)}
        </span>
      </p>

      {plan === null ? null : (
        <p className="text-xs text-secondary-text">{plan}</p>
      )}

      {declined.length === 0 ? (
        <p className="text-xs text-secondary-text">
          They took the whole proposal as offered.
        </p>
      ) : (
        <div className="space-y-0.5">
          <p className="text-xs text-secondary-text">
            {deferredLabel}
            {selection.deferredAmount === null
              ? ''
              : `, worth ${formatMoneyAmount(selection.deferredAmount)}`}
            :
          </p>
          <ul className="space-y-0.5">
            {declined.map((line) => (
              <li key={line} className="text-xs text-secondary-text">
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
